import React, { useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import type { DeviceSummary } from '@/entities/device/types';
import { playableProfiles, type StreamCamera } from '@/entities/stream/types';
import { useBreakpoint } from '@/shared/lib/breakpoints';
import { fonts } from '@/shared/ui/fonts';
import { Icon, type IconName } from '@/shared/ui/icons';
import { AppButton } from '@/shared/ui/button';
import { AppInput } from '@/shared/ui/input';
import { Menu, MenuOption, useAnchor } from '@/shared/ui/menu';
import { AppText } from '@/shared/ui/text';
import { spacing, useTheme } from '@/shared/ui/theme';
import { CAN_DRAG, useDragSource, useHoverWithin } from '../lib/tile-dom';
import type { TreeGroup } from '../lib/catalog';
import type { GroupBy } from '../lib/view-store';

/**
 * Дерево камер слева от раскладки — «Список ресурсов» iVMS-4200.
 *
 * Регистратор — узел, его каналы — листья; отдельные камеры и терминалы — своими
 * группами. Нажатие на камеру открывает её в ВЫБРАННОМ окне раскладки, и выделение
 * само переходит к следующему пустому окну — камеры раскладываются щелчками подряд.
 * ▶ у группы открывает все её камеры разом. На вебе камеру можно и перетащить мышью
 * в любое окно.
 *
 * ## Оформление — по ui-kit
 *
 * Панель рабочей станции, а не список карточек: строки по 30pt встык, тонкие линии,
 * серый везде, кроме одного места. Красное — только риска у камер, которые сейчас
 * в окнах (то же, что риска активной вкладки в ui-kit), и подчёркивание выбранного
 * способа группировки. Номер окна — серой цифрой моноширинным: его сверяют с
 * номером в углу окна, а не замечают издалека.
 *
 * Кнопки строки (▶, ⚙) появляются под курсором: двенадцать шестерёнок столбиком —
 * это шум, который читается раньше названий камер. На телефоне курсора нет, и там они
 * видны всегда.
 */
export type { TreeGroup } from '../lib/catalog';

const ROW = 30;
/** С какого числа камер поиск стоит открытым сам. */
const SEARCH_FROM = 8;

const styles = StyleSheet.create({
  root: { flex: 1, minHeight: 0 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    height: 40,
    paddingLeft: spacing(3),
    paddingRight: spacing(1),
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerTitle: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: spacing(2),
  },
  search: { paddingHorizontal: spacing(2), paddingTop: spacing(2) },
  tabs: {
    flexDirection: 'row',
    paddingHorizontal: spacing(2),
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  tab: {
    height: 34,
    paddingHorizontal: spacing(2),
    justifyContent: 'center',
    borderBottomWidth: 2,
    marginBottom: -StyleSheet.hairlineWidth,
  },
  list: { paddingVertical: spacing(1), paddingBottom: spacing(4) },
  group: { marginBottom: spacing(1) },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    height: ROW + 2,
    paddingRight: spacing(1),
  },
  headToggle: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    paddingLeft: spacing(2),
  },
  headText: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: spacing(2),
  },
  headTitle: { flexShrink: 1 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    height: ROW,
    paddingLeft: 30,
    paddingRight: spacing(1),
  },
  risk: { position: 'absolute', left: 0, top: 4, bottom: 4, width: 2 },
  rowText: { flex: 1, minWidth: 0, fontSize: 13, lineHeight: 18 },
  slot: {
    minWidth: 16,
    textAlign: 'right',
    fontFamily: fonts.mono.regular,
    fontSize: 11,
  },
  dot: { width: 6, height: 6, borderRadius: 3 },
  tool: {
    width: 26,
    height: 26,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 3,
  },
  hint: {
    paddingLeft: 30,
    paddingRight: spacing(3),
    paddingVertical: spacing(1),
  },
  empty: { padding: spacing(4), alignItems: 'center', gap: spacing(2) },
});

type Hot = { pressed: boolean; hovered?: boolean };

function isHot(state: { pressed: boolean }): boolean {
  const s = state as Hot;
  return s.pressed || s.hovered === true;
}

/**
 * Строка со своими кнопками внутри (настройки, ▶), которые видны под курсором.
 *
 * Не `hovered` из `Pressable`: в react-native-web вложенный `Pressable` при наведении
 * гасит hover родителя, и кнопка исчезала, как только к ней подводили курсор.
 * `useHoverWithin` считает кнопки частью строки.
 */
function HoverRow({
  rowRef,
  style,
  children,
  ...rest
}: Omit<React.ComponentProps<typeof Pressable>, 'style' | 'children'> & {
  rowRef?: React.RefObject<View | null>;
  style: (hot: boolean) => React.ComponentProps<typeof View>['style'];
  children: (hot: boolean) => React.ReactNode;
}) {
  const own = useRef<View>(null);
  const ref = rowRef ?? own;
  const hover = useHoverWithin(ref);
  return (
    <Pressable
      ref={ref}
      {...rest}
      style={state => style(hover || state.pressed)}
    >
      {state => children(hover || state.pressed)}
    </Pressable>
  );
}

/** Точка состояния: зелёная — в сети, серая — нет, пустая — пропала из отчёта. */
function Dot({ camera }: { camera: StreamCamera }) {
  const p = useTheme();
  const color = !camera.available
    ? 'transparent'
    : camera.online
    ? p.signal
    : p.mutedForeground;
  return (
    <View
      style={[
        styles.dot,
        {
          backgroundColor: color,
          borderWidth: camera.available ? 0 : 1,
          borderColor: p.mutedForeground,
        },
      ]}
    />
  );
}

/** Маленькая кнопка строки — значок без рамки, подпись под курсором. */
function Tool({
  icon,
  label,
  onPress,
  testID,
}: {
  icon: IconName;
  label: string;
  onPress: () => void;
  testID?: string;
}) {
  const p = useTheme();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      {...({ title: label } as object)}
      onPress={onPress}
      hitSlop={4}
      style={state => [
        styles.tool,
        isHot(state) && { backgroundColor: p.active },
      ]}
      testID={testID}
    >
      <Icon name={icon} size={14} color={p.mutedForeground} />
    </Pressable>
  );
}

/** Камера в дереве: нажать — в выбранное окно, перетащить — в любое. */
function TreeCamera({
  camera,
  slot,
  onPick,
  onSettings,
  alwaysTools,
}: {
  camera: StreamCamera;
  slot: number | undefined;
  onPick: (camera: StreamCamera) => void;
  onSettings?: (camera: StreamCamera) => void;
  alwaysTools: boolean;
}) {
  const { t } = useTranslation();
  const p = useTheme();
  const ref = useRef<View>(null);
  useDragSource(ref, CAN_DRAG ? { kind: 'camera', id: camera.deviceId } : null);
  const name = camera.name || camera.deviceId;
  const playable = camera.available && playableProfiles(camera).length > 0;
  const status = !camera.available
    ? t('live.offline')
    : camera.online
    ? t('live.online')
    : t('live.notConnected');
  const open = slot !== undefined;
  return (
    <HoverRow
      rowRef={ref}
      testID={`live-tree-${camera.deviceId}`}
      accessibilityLabel={name}
      // Состояние словом под курсором: серая точка сама не говорит, почему серая.
      {...({ title: `${name} — ${status}` } as object)}
      onPress={() => onPick(camera)}
      style={hot => [styles.row, hot && { backgroundColor: p.accent }]}
    >
      {hot => (
        <>
          <View
            style={[
              styles.risk,
              { backgroundColor: open ? p.primary : 'transparent' },
            ]}
          />
          <Dot camera={camera} />
          <AppText
            numberOfLines={1}
            color={
              open
                ? p.foreground
                : playable
                ? p.textSecondary
                : p.mutedForeground
            }
            style={[styles.rowText, { fontFamily: fonts.sans.regular }]}
          >
            {name}
          </AppText>
          {onSettings && (alwaysTools || hot) ? (
            <Tool
              icon="settings"
              label={t('camera.settingsTitle')}
              onPress={() => onSettings(camera)}
            />
          ) : null}
          {open ? (
            <AppText color={p.mutedForeground} style={styles.slot}>
              {slot + 1}
            </AppText>
          ) : null}
        </>
      )}
    </HoverRow>
  );
}

export function CameraTree({
  groups,
  pending,
  openIn,
  onPick,
  onPickMany,
  onCameraSettings,
  onPendingSettings,
  onAdd,
  onAdopt,
  onGroupSettings,
  groupBy,
  onGroupBy,
  scroll = true,
}: {
  groups: TreeGroup[];
  /** Настройки устройства-узла группы (регистратора). */
  onGroupSettings?: (sourceId: string, name: string) => void;
  /** По устройствам — как стоят коробки; по отделам — как устроена компания. */
  groupBy: GroupBy;
  onGroupBy: (next: GroupBy) => void;
  /** Камеры компании, ни разу не выходившие на связь. */
  pending: DeviceSummary[];
  /** Камера → номер окна (с нуля), где она открыта. */
  openIn: ReadonlyMap<string, number>;
  onPick: (camera: StreamCamera) => void;
  onPickMany: (cameras: StreamCamera[]) => void;
  /** Настройки отдельной камеры (не канала регистратора — те у его узла). */
  onCameraSettings?: (camera: StreamCamera) => void;
  onPendingSettings?: (source: DeviceSummary) => void;
  onAdd: () => void;
  /** Подключить устройство по QR (регистратор, терминал) — второй пункт «+». */
  onAdopt?: () => void;
  /** Своя прокрутка — в колонке на широком окне; на телефоне листает страница. */
  scroll?: boolean;
}) {
  const { t } = useTranslation();
  const p = useTheme();
  const { isDesktop } = useBreakpoint();
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const add = useAnchor();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());

  const needle = query.trim().toLowerCase();
  const matches = (name: string) =>
    !needle || name.toLowerCase().includes(needle);
  const toggle = (key: string) =>
    setCollapsed(cur => {
      const next = new Set(cur);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const visible = groups
    .map(g => ({
      ...g,
      cameras: matches(g.title)
        ? g.cameras
        : g.cameras.filter(c => matches(c.name || c.deviceId)),
    }))
    .filter(g => !needle || g.cameras.length > 0 || matches(g.title));
  const pendingVisible = pending.filter(s =>
    matches(s.device_name || s.device_id),
  );

  const all = groups.flatMap(g => g.cameras);
  const searchable = all.length + pending.length > SEARCH_FROM;
  const onlineCount = all.filter(c => c.available && c.online).length;

  const renderCamera = (camera: StreamCamera, group: TreeGroup) => (
    <TreeCamera
      key={camera.deviceId}
      camera={camera}
      slot={openIn.get(camera.deviceId)}
      onPick={onPick}
      alwaysTools={!isDesktop}
      onSettings={
        // Канал регистратора настраивается в его регистраторе — там же его отдел
        // («Отделы камер»). Отдельная камера — сама по себе.
        // На телефоне у каналов её нет: шестерёнка регистратора стоит строкой выше и
        // ведёт туда же, а столбик одинаковых значков — это шум.
        group.settings
          ? onGroupSettings && isDesktop
            ? () =>
                onGroupSettings(group.settings!.sourceId, group.settings!.name)
            : undefined
          : onCameraSettings
      }
    />
  );

  const content = (
    <>
      {visible.map(group => {
        const open = !collapsed.has(group.key) || !!needle;
        const online = group.cameras.filter(
          c => c.available && c.online,
        ).length;
        return (
          <View key={group.key} style={styles.group}>
            {/* Строка — не кнопка целиком: в ней свои кнопки (▶, настройки), а
                кнопка в кнопке — невалидный HTML. Сворачивает левая часть. */}
            <HoverRow
              onPress={() => {}}
              style={hot => [styles.head, hot && { backgroundColor: p.accent }]}
            >
              {hot => (
                <>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityState={{ expanded: open }}
                    accessibilityLabel={group.title}
                    onPress={() => toggle(group.key)}
                    style={styles.headToggle}
                  >
                    <Icon
                      name={open ? 'chevronDown' : 'chevron'}
                      size={12}
                      color={p.mutedForeground}
                    />
                    <Icon name={group.icon} size={14} color={p.textSecondary} />
                    <View style={styles.headText}>
                      <AppText
                        variant="label"
                        numberOfLines={1}
                        color={p.foreground}
                        style={styles.headTitle}
                      >
                        {group.title}
                      </AppText>
                    </View>
                  </Pressable>
                  {group.online !== undefined ? (
                    <View
                      style={[
                        styles.dot,
                        {
                          backgroundColor: group.online
                            ? p.signal
                            : p.mutedForeground,
                        },
                      ]}
                    />
                  ) : null}
                  {(hot || !isDesktop) && group.cameras.length > 0 ? (
                    <Tool
                      icon="play"
                      label={t('live.view.openGroup')}
                      onPress={() => onPickMany(group.cameras)}
                      testID={`live-tree-play-${group.key}`}
                    />
                  ) : null}
                  {(hot || !isDesktop) && group.settings && onGroupSettings ? (
                    <Tool
                      icon="settings"
                      label={t('camera.settingsTitle')}
                      onPress={() =>
                        onGroupSettings(
                          group.settings!.sourceId,
                          group.settings!.name,
                        )
                      }
                    />
                  ) : null}
                  {group.cameras.length > 0 ? (
                    <AppText
                      color={p.mutedForeground}
                      style={[styles.slot, { minWidth: 28 }]}
                    >
                      {`${online}/${group.cameras.length}`}
                    </AppText>
                  ) : null}
                </>
              )}
            </HoverRow>
            {open ? (
              group.cameras.length > 0 ? (
                group.cameras.map(c => renderCamera(c, group))
              ) : group.emptyHint ? (
                <View style={styles.hint}>
                  <AppText variant="micro">{group.emptyHint}</AppText>
                </View>
              ) : null
            ) : null}
          </View>
        );
      })}

      {pendingVisible.length > 0 ? (
        <View style={styles.group}>
          <View style={[styles.head, styles.headToggle]}>
            <Icon name="clock" size={14} color={p.mutedForeground} />
            <AppText variant="label" color={p.textSecondary} numberOfLines={1}>
              {t('live.awaitingFirst')}
            </AppText>
          </View>
          {pendingVisible.map(source => (
            <Pressable
              key={source.device_id}
              accessibilityRole="button"
              {...({ title: t('live.awaitingFirstHint') } as object)}
              onPress={() => onPendingSettings?.(source)}
              style={state => [
                styles.row,
                isHot(state) && { backgroundColor: p.accent },
              ]}
            >
              <View style={[styles.dot, { backgroundColor: p.warning }]} />
              <AppText
                numberOfLines={1}
                color={p.mutedForeground}
                style={[styles.rowText, { fontFamily: fonts.sans.regular }]}
              >
                {source.device_name || source.device_id}
              </AppText>
              <Icon name="settings" size={14} color={p.mutedForeground} />
            </Pressable>
          ))}
        </View>
      ) : null}

      {needle && visible.length === 0 && pendingVisible.length === 0 ? (
        <View style={styles.empty}>
          <Icon name="search" size={18} color={p.mutedForeground} />
          <AppText variant="caption">{t('live.view.nothingFound')}</AppText>
        </View>
      ) : null}
    </>
  );

  const tabs: { value: GroupBy; label: string }[] = [
    { value: 'device', label: t('live.view.byDevice') },
    { value: 'department', label: t('live.view.byDepartment') },
  ];

  return (
    <View style={styles.root} testID="live-tree">
      <View style={[styles.header, { borderBottomColor: p.border }]}>
        <View style={styles.headerTitle}>
          <AppText variant="label" color={p.foreground}>
            {t('live.title')}
          </AppText>
          <AppText color={p.mutedForeground} style={styles.slot}>
            {`${onlineCount}/${all.length}`}
          </AppText>
        </View>
        {searchable ? null : (
          <Tool
            icon="search"
            label={t('live.view.search')}
            onPress={() => setSearching(v => !v)}
            testID="live-tree-search-toggle"
          />
        )}
        <View ref={add.ref} collapsable={false}>
          {/* Словом, а не голым «+»: серый значок в углу не нашли (владелец,
              2026-10-22). Обводкой — главная кнопка экрана всё же видео. */}
          <AppButton
            title={t('live.view.add')}
            icon="plus"
            tone="outline"
            size="sm"
            onPress={() =>
              onAdopt ? add.measure(() => setAddOpen(true)) : onAdd()
            }
            testID="live-tree-add"
          />
        </View>
      </View>
      {/* Поиск — когда есть что искать: в списке из трёх камер поле ввода выше
          самого списка. Иначе он открывается значком в шапке. */}
      {searchable || searching ? (
        <View style={styles.search}>
          <AppInput
            value={query}
            onChangeText={setQuery}
            icon="search"
            placeholder={t('live.view.search')}
            size="md"
            autoFocus={searching && !searchable}
            testID="live-tree-search"
          />
        </View>
      ) : null}
      <Menu
        visible={addOpen}
        onClose={() => setAddOpen(false)}
        anchor={add.anchor}
        title={t('camera.add.title')}
        testID="live-tree-add-menu"
      >
        <MenuOption
          icon="camera"
          label={t('camera.add.title')}
          selected={false}
          onPress={() => {
            setAddOpen(false);
            onAdd();
          }}
          testID="live-tree-add-camera"
        />
        {onAdopt ? (
          <MenuOption
            icon="qr"
            label={t('home.adopt.cta')}
            hint={t('live.view.adoptHint')}
            selected={false}
            onPress={() => {
              setAddOpen(false);
              onAdopt();
            }}
            testID="live-tree-adopt"
          />
        ) : null}
      </Menu>
      <View
        style={[styles.tabs, { borderBottomColor: p.border }]}
        testID="live-tree-group"
      >
        {tabs.map(tab => {
          const on = tab.value === groupBy;
          return (
            <Pressable
              key={tab.value}
              accessibilityRole="tab"
              accessibilityState={{ selected: on }}
              onPress={() => onGroupBy(tab.value)}
              style={[
                styles.tab,
                { borderBottomColor: on ? p.primary : 'transparent' },
              ]}
              testID={`live-tree-group-${tab.value}`}
            >
              <AppText
                variant="label"
                color={on ? p.foreground : p.mutedForeground}
              >
                {tab.label}
              </AppText>
            </Pressable>
          );
        })}
      </View>
      {scroll ? (
        <ScrollView
          contentContainerStyle={styles.list}
          showsVerticalScrollIndicator
        >
          {content}
        </ScrollView>
      ) : (
        <View style={styles.list}>{content}</View>
      )}
    </View>
  );
}
